/**
 * defa.controllers.CustomerController
 * The Controller for the CustomerRouter, all front end urls starting with /billing/.
 * @version 1.2.1
 */
import { defa }          from 'defa-object';
import Customer          from 'scripts/defa/customers/model-customer';
import CustomerContainer from 'scripts/defa/customers/view-customer-container';
import Invoices          from 'scripts/defa/invoices/collection-invoices';
import User              from 'scripts/defa/users/model-user';
import Controller        from 'scripts/poppet/controller';

const channelLayout = Bb.Radio.channel( 'layout' ),
	channelNav    = Bb.Radio.channel( 'nav' ),
	channelUser   = Bb.Radio.channel( 'user' );

const channel = Bb.Radio.channel( 'objects' );

const CustomerController = Controller.extend( {
	
	// ------------------------------------------------
	// Routes
	// ------------------------------------------------
	
	/**
	 * Shows a list of all customers.
	 * @param {int} userId  The ID of the user to get the customers from.
	 * @void
	 */
	showCustomerList: function ( userId ) {
		
		let user = channelUser.request( 'model' );
		
		// check for me
		let currentUser = ( parseInt( userId ) === user.get( 'id' ) )
						  ? user
						  : new User( { id: userId } );
		
		// on user, show layout
		this.listenToOnce( currentUser, 'sync complete', function ( model ) {
			
			this.stopListening( currentUser );
			
			this.attachCustomerContainer( {
				
				view   : 'customer:list',
				context: 'billing',
				create : true,
				
				currentUser: model,
				userId     : model.get( 'id' ),
				customers  : model.getCustomers(),
				
				closeLink: {
					event : 'user:panel',
					params: [ model.get( 'id' ) ],
				},
			} );
		} );
		
		// on no user, show error
		this.listenToOnce( currentUser, 'error', function () {
			channelLayout.trigger( 'error', { model: currentUser } );
		} );
		
		// load user
		if ( !currentUser.isComplete() ) {
			currentUser.fetch();
		} else {
			currentUser.trigger( 'complete', currentUser );
		}
	},
	
	/**
	 * Shows the Create new customer view.
	 * @void
	 */
	showCustomerCreate: function ( userId ) {
		let user = channelUser.request( 'model' );
		
		// check for me
		let currentUser = ( userId === user.get( 'id' ) )
						  ? user
						  : new User( { id: userId } );
		
		// on user, show layout
		this.listenToOnce( currentUser, 'sync complete', function ( model ) {
			this.stopListening( currentUser );
			
			let customer = new Customer();
			
			this.attachCustomerContainer( {
				view       : 'customer:create',
				userId     : model.get( 'id' ),
				currentUser: model,
				customer   : customer,
				closeLink  : {
					event : 'user:panel',
					params: [ model.get( 'id' ), ],
				},
			} );
		} );
		
		// load user
		if ( !currentUser.isComplete() ) {
			currentUser.fetch();
		} else {
			currentUser.trigger( 'complete' );
		}
	},
	
	/**
	 * Shows a single customer details view.
	 * @param {int} id  The id of the customer.
	 * @void
	 */
	showCustomerUpdate: function ( id ) {
		// no closelink as support might not know the user.
		this.requireCustomer( {
			view      : 'customer:update',
			customerId: id,
			create    : true,
			callback  : this.attachCustomerContainer,
			sync      : false,
		} );
	},
	
	/**
	 * Displays the customers invoices.
	 * @void
	 */
	showCustomerInvoices: function ( id ) {
		
		let invoices = new Invoices();
		
		this.listenToOnce( invoices, 'sync', function () {
			
			this.attachCustomerContainer( {
				view     : 'customer:invoices',
				invoices : invoices,
				closeLink: {
					event: 'app:home',
				},
			} );
			
		} );
		
		invoices.fetchCustomerInvoices( {
			customerId: id,
		} );
	},
	
	/**
	 * Displays an invoice detail with update functionality.
	 * @param {number} id  The invoice id.
	 * @void
	 */
	showInvoice: function ( id ) {
		
		this.requireInvoice( {
			view     : 'invoice:update',
			invoiceId: id,
			callback : this.attachCustomerContainer,
			complete : true,
			create   : false,
			sync     : false,
			closeLink: {
				event : 'customer:invoices',
				params: [], // NOTE: We don't know which customer it is yet, so we'll push the customerId into the
				// params in the CustomerContainer._showInvoiceUpdate method.
			}
		} );
	},
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	// - model handlers
	
	/**
	 * Triggers when a new customer is created.
	 * @param {Customer} customer  The Customer model being saved.
	 * @void
	 */
	onCustomerSync: function ( modelIn ) {
		
		let model    = channel.request( 'customers' ).add( modelIn, { merge: true } ),
			user     = model.get( 'transientUser' ) || false,
			customer = model.get( 'id' );
		
		// add a mapping if this customer has none.
		if ( 1 > _.where( defa.vars.mapUsersCustomers, { customer: customer, } ).length ) {
			defa.vars.mapUsersCustomers.push( {
				user    : user,
				customer: customer,
				roles   : [ 'CUSTOMER', ],
			} );
		}
		
		// now retrieve which user is mapped to this customer
		let map = _.where( defa.vars.mapUsersCustomers, { customer: customer, } );
		user    = _.first( map ).user;
		
		// and exit
		this.customerContainer.animateChildOut( {
			closeLink: {
				event : 'customer:list',
				params: [ user ],
			},
		} );
	},
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Requires an Invoice object before continuing it's callback.
	 * @param {Object} options All the needed options, most importantly an invoiceId and a callback function.
	 * @void
	 */
	requireInvoice: function ( options ) {
		this.requireModel( 'Invoice', defa.models, options );
	},
	
	/**
	 * Requires a Customer object before continuing it's callback.
	 * @param {Object} options All the needed options, most importantly a customerId and a callback function.
	 * @void
	 */
	requireCustomer: function ( options ) {
		this.requireModel( 'Customer', defa.models, options );
	},
	
	/**
	 * Does dispatch logic on the User's login-state or its lack of customer objects. Then creates and displays a
	 * CustomerContainer.
	 * @param {Object} options  An object containing all the needed objects for creating, listing and editing a
	 *     users customers collection.
	 * @void
	 */
	attachCustomerContainer: function ( options ) {
		
		let user = channelUser.request( 'model' );
		
		options      = options || {};
		options.user = options.user || user;
		
		// if the user isn't logged in, send to login
		if ( channelUser.request( 'is:logged:out' ) ) {
			channelNav.trigger( 'user:login', false, { trigger: true, replace: true, } );
			
			// if the user is logged in, play with the customerContainer.
		} else {
			let self = options.self || this;
			
			// if the customerlayout isn't made or is destroyed, create it and attach it's listeners!
			if ( !( self.customerContainer instanceof CustomerContainer ) || self.customerContainer.isDestroyed() ) {
				self.customerContainer = new CustomerContainer( options );
				
				self.listenTo( self.customerContainer, 'destroy', function () {
					self.stopListening();
				} );
				
				// if it already exists, just update it's options.
			} else {
				self.customerContainer.settings( options );
			}
			
			// create a new sync listener as long as sync isn't set to false
			if ( options.customer ) {
				
				self.stopListening( options.customer, 'sync', self.onCustomerSync );
				
				if ( !_.isBoolean( options.sync ) || options.sync ) {
					self.listenTo( options.customer, 'sync', self.onCustomerSync );
				}
			}
			
			// if it's not shown yet, show it!
			if ( !self.customerContainer.isAttached() ) {
				channelLayout.request( 'root' ).showChildView( 'main', self.customerContainer );
			}
		}
	},
	
} );

export default CustomerController;